import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GOOGLE_AUTH } from '../../store/constants';
import { getGoogleClientId, initializeGoogleIdentity } from '../../utils/googleIdentity';
import './style.css';

function GoogleAuthButton({ returnUrl }) {
	const dispatch = useDispatch();
	const userActions = useSelector((state) => state.userActions);
	const wrapRef = useRef(null);
	const containerRef = useRef(null);
	const [localError, setLocalError] = useState('');
	const [isReady, setIsReady] = useState(false);

	const googleError = userActions?.googleAuthError ? userActions?.message?.message || userActions?.message : '';
	const isLoading = Boolean(userActions?.isLoading);

	useEffect(() => {
		let isMounted = true;

		async function renderGoogleButton() {
			if (!getGoogleClientId()) {
				setLocalError('Google sign-in is currently unavailable.');
				return;
			}

			try {
				const google = await initializeGoogleIdentity(({ credential }) => {
					if (!credential) {
						setLocalError('Google sign-in did not complete. Please try again.');
						return;
					}

					setLocalError('');
					dispatch({
						type: GOOGLE_AUTH,
						payload: {
							credential,
							returnUrl
						}
					});
				});

				if (!isMounted || !google || !containerRef.current) {
					return;
				}

				const availableWidth = wrapRef.current?.offsetWidth || 360;
				const buttonWidth = Math.min(availableWidth, 350);
				containerRef.current.style.width = `${buttonWidth}px`;
				containerRef.current.innerHTML = '';
				google.accounts.id.renderButton(containerRef.current, {
					type: 'standard',
					theme: 'outline',
					size: 'large',
					text: 'continue_with',
					shape: 'rectangular',
					logo_alignment: 'left',
					width: buttonWidth
				});
				setIsReady(true);
			} catch (error) {
				if (isMounted) {
					setLocalError('Google sign-in is currently unavailable.');
				}
			}
		}

		renderGoogleButton();

		return () => {
			isMounted = false;
			if (containerRef.current) {
				containerRef.current.innerHTML = '';
			}
		};
	}, [dispatch, returnUrl]);

	return (
		<div className='googleAuthSection'>
			<div ref={wrapRef} className={`googleAuthButtonWrap${isLoading ? ' is-loading' : ''}`}>
				<div ref={containerRef} className='googleAuthButtonFrame' />
				{!isReady && !localError ? <div className='googleAuthLoadingOverlay' aria-live='polite'>Loading Google sign-in...</div> : null}
				{isLoading ? <div className='googleAuthLoadingOverlay' aria-live='polite'>Connecting...</div> : null}
			</div>
			{localError || googleError ? <div className='googleAuthError'>{localError || googleError}</div> : null}
		</div>
	);
}

export default GoogleAuthButton;
